import React, { useState } from 'react';

const CAOArchitectureDiagram = () => {
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  const nodeDetails = {
    inputs: {
      title: 'Input Channels',
      description: 'Multi-modal intake supporting messaging, voice, text, and API integrations',
      items: ['WhatsApp, Telegram, SMS', 'Speech-to-Text, Commands', 'REST, Webhooks, SDK']
    },
    orchestrator: {
      title: 'Executive Orchestrator',
      description: 'Central coordination layer handling classification, routing, and conflict resolution',
      items: ['Intake Normalization', 'Risk Classification', 'Council Routing', 'Conflict Detection']
    },
    ceo: {
      title: 'CEO Agent',
      description: 'Strategy & Risk Authority',
      veto: 'Strategic Deadlock',
      color: '#3B82F6'
    },
    cpo: {
      title: 'CPO Agent',
      description: 'Product Intent Authority',
      veto: 'User Harm',
      color: '#8B5CF6'
    },
    cto: {
      title: 'CTO Agent',
      description: 'Architecture & Security Authority',
      veto: 'Security / Reliability',
      color: '#06B6D4'
    },
    cao: {
      title: 'CAO Agent',
      description: 'Behavior, Trust & Autonomy Authority',
      veto: 'Autonomy / Safety',
      color: '#10B981'
    },
    behavioral_gate: {
      title: 'CAO Behavioral Gate',
      subtitle: 'Final Authority',
      description: 'Hard governance boundary ensuring safe, reversible, and auditable execution',
      constraints: [
        'Cannot self-escalate autonomy',
        'Cannot remove safeguards',
        'Must log all override rationale'
      ],
      authority: [
        'Approve / deny autonomy tiers',
        'Pause or rollback agents',
        'Block irreversible actions'
      ]
    },
    policy: {
      title: 'Policy Engine',
      description: 'Translates executive decisions into enforceable system rules',
      items: ['ABAC + Guardrails', 'Autonomy Tiers', 'Approval Gates', 'Tool Constraints']
    },
    gateway: {
      title: 'Tool Gateway',
      description: 'Single execution choke point - no agent executes tools directly',
      items: ['Short-Lived Credentials', 'Strict Allowlists', 'Spend / Scope Limits']
    },
    external: {
      title: 'External APIs / Tools',
      items: ['Anthropic', 'ElevenLabs', 'OpenRouter', 'Custom Integrations']
    },
    outputs: {
      title: 'Proactive Outputs',
      items: ['Calendar Events', 'Compliance Reports', 'Anomalies & Alerts']
    },
    audit: {
      title: 'Audit & Telemetry Layer',
      description: 'Immutable, exportable, enterprise-ready logging',
      compliance: ['SOC-2', 'ISO', 'GDPR'],
      coverage: ['Decisions', 'Overrides', 'Tool Execution', 'Autonomy Changes']
    }
  };

  const GlowOrb = ({ x, y, color, size = 100, opacity = 0.3 }) => (
    <div
      className="absolute rounded-full blur-3xl pointer-events-none"
      style={{
        left: x,
        top: y,
        width: size,
        height: size,
        background: color,
        opacity,
        transform: 'translate(-50%, -50%)'
      }}
    />
  );

  const NodeCard = ({ id, children, className = '', glowColor = '#4ADE80', onClick }) => {
    const isHovered = hoveredNode === id;
    const isSelected = selectedNode === id;

    const handleInteraction = (e) => {
      e.preventDefault();
      setSelectedNode(isSelected ? null : id);
    };

    return (
      <div
        className={`relative transition-all duration-300 cursor-pointer ${className}`}
        onMouseEnter={() => setHoveredNode(id)}
        onMouseLeave={() => setHoveredNode(null)}
        onClick={handleInteraction}
        onTouchEnd={handleInteraction}
        role="button"
        tabIndex={0}
        aria-expanded={isSelected}
        onKeyDown={(e) => e.key === 'Enter' && handleInteraction(e)}
        style={{
          transform: isHovered ? 'scale(1.02)' : 'scale(1)',
        }}
      >
        <div
          className="absolute inset-0 rounded-2xl blur-xl transition-opacity duration-300"
          style={{
            background: glowColor,
            opacity: isHovered ? 0.3 : 0.1
          }}
        />
        <div
          className={`relative rounded-2xl border backdrop-blur-xl transition-all duration-300 ${
            isSelected ? 'ring-2' : ''
          }`}
          style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.8) 100%)',
            borderColor: isHovered ? glowColor : 'rgba(148, 163, 184, 0.2)',
            ringColor: glowColor
          }}
        >
          {children}
        </div>
      </div>
    );
  };

  const AgentCard = ({ id, title, subtitle, veto, color }) => (
    <NodeCard id={id} glowColor={color} className="flex-1 min-w-[140px]">
      <div className="p-4 text-center">
        <div className="w-10 h-10 mx-auto mb-2 rounded-xl flex items-center justify-center"
          style={{ background: `${color}20`, border: `1px solid ${color}40` }}>
          <div className="w-3 h-3 rounded-full" style={{ background: color, boxShadow: `0 0 12px ${color}` }} />
        </div>
        <h3 className="text-sm font-semibold text-white mb-1">{title}</h3>
        <p className="text-xs text-slate-400 mb-2">{subtitle}</p>
        <div className="text-[10px] px-2 py-1 rounded-full"
          style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}>
          VETO: {veto}
        </div>
      </div>
    </NodeCard>
  );

  const ConnectionLine = ({ vertical = true }) => (
    <div className={`flex ${vertical ? 'flex-col' : 'flex-row'} items-center justify-center ${vertical ? 'h-8' : 'w-8'}`}>
      <div className={`${vertical ? 'w-0.5 h-full' : 'h-0.5 w-full'} bg-gradient-to-b from-emerald-500/50 via-emerald-400/80 to-emerald-500/50`} />
      <div className={`${vertical ? 'w-0 h-0 border-l-4 border-r-4 border-t-8' : 'w-0 h-0 border-t-4 border-b-4 border-l-8'} border-l-transparent border-r-transparent border-t-emerald-400/80`} />
    </div>
  );

  const Badge = ({ children, color = '#4ADE80' }) => (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium mr-1 mb-1"
      style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}
    >
      {children}
    </span>
  );

  return (
    <div className="w-full p-4 md:p-6 overflow-auto rounded-2xl" style={{
      background: 'linear-gradient(180deg, #0B0F1A 0%, #111827 50%, #0B0F1A 100%)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
        <GlowOrb x="10%" y="20%" color="#3B82F6" size={400} opacity={0.08} />
        <GlowOrb x="90%" y="30%" color="#8B5CF6" size={350} opacity={0.08} />
        <GlowOrb x="50%" y="60%" color="#10B981" size={500} opacity={0.06} />
        <GlowOrb x="20%" y="80%" color="#06B6D4" size={300} opacity={0.06} />

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
            style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-emerald-400 text-sm font-medium tracking-wide">Interactive Architecture</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 tracking-tight">
            CAO Platform — Architecture
          </h2>
          <p className="text-slate-400 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Executive-governed delegation system where autonomy is <span className="text-emerald-400">earned</span>, <span className="text-cyan-400">audited</span>, and <span className="text-violet-400">never self-authorized</span>
          </p>
        </div>

        {/* Architecture Flow */}
        <div className="flex flex-col gap-3">

          {/* Input Channels */}
          <NodeCard id="inputs" glowColor="#60A5FA">
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-2 h-2 rounded-full bg-blue-400" />
                <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider">Input Channels</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { icon: '\u{1F4AC}', label: 'Messaging', sub: 'WhatsApp \u2022 Telegram \u2022 SMS' },
                  { icon: '\u{1F399}', label: 'Voice', sub: 'Speech-to-Text \u2022 Commands' },
                  { icon: '\u{1F4DD}', label: 'Text', sub: 'REST \u2022 Webhooks \u2022 SDK' },
                  { icon: '\u{1F50C}', label: 'API', sub: 'REST \u2022 SDK' }
                ].map((item, i) => (
                  <div key={i} className="px-3 py-2 rounded-xl bg-slate-800/50 border border-slate-700/50">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{item.icon}</span>
                      <span className="text-white text-sm font-medium">{item.label}</span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">{item.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </NodeCard>

          <ConnectionLine />

          {/* Executive Orchestrator */}
          <NodeCard id="orchestrator" glowColor="#A78BFA">
            <div className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-violet-400" />
                  <h3 className="text-sm font-semibold text-violet-400 uppercase tracking-wider">Executive Orchestrator</h3>
                </div>
                <div className="flex flex-wrap gap-1">
                  {['Classification', 'Routing', 'Coordination'].map((item, i) => (
                    <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                      style={{ background: 'rgba(167, 139, 250, 0.15)', color: '#A78BFA', border: '1px solid rgba(167, 139, 250, 0.3)' }}>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </NodeCard>

          <ConnectionLine />

          {/* Executive Agent Council */}
          <div>
            <div className="text-center mb-3">
              <span className="text-[11px] font-semibold text-amber-500/80 uppercase tracking-widest">
                Executive Agent Council \u2014 Deliberation Layer
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <AgentCard id="ceo" title="CEO" subtitle="Strategy & Risk" veto="Strategic Deadlock" color="#3B82F6" />
              <AgentCard id="cpo" title="CPO" subtitle="Product Intent" veto="User Harm" color="#8B5CF6" />
              <AgentCard id="cto" title="CTO" subtitle="Architecture & Security" veto="Security / Reliability" color="#06B6D4" />
              <AgentCard id="cao" title="CAO" subtitle="Nellie" veto="Autonomy / Safety" color="#10B981" />
            </div>
          </div>

          <ConnectionLine />

          {/* CAO Behavioral Gate */}
          <NodeCard id="behavioral_gate" glowColor="#10B981" className="relative">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <div className="px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40">
                <span className="text-emerald-400 text-[10px] font-bold uppercase tracking-wider">Final Authority</span>
              </div>
            </div>
            <div className="p-6 pt-8">
              <div className="flex flex-col md:flex-row items-center gap-6">
                {/* CAO Logo/Icon */}
                <div className="relative flex-shrink-0">
                  <div className="w-24 h-24 rounded-2xl flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(6, 182, 212, 0.1) 100%)',
                      border: '2px solid rgba(16, 185, 129, 0.4)'
                    }}>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-emerald-400">CAO</div>
                      <div className="text-[10px] text-slate-400">Behavioral Gate</div>
                    </div>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-slate-900 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-lg font-bold text-white mb-1">Chief Agentic Officer</h3>
                  <p className="text-slate-400 text-sm mb-4">Hard governance boundary ensuring safe, reversible, and auditable execution</p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-[10px] uppercase tracking-wider text-red-400/80 font-semibold mb-2">Constraints</h4>
                      <ul className="space-y-1">
                        {['Cannot self-escalate autonomy', 'Cannot remove safeguards', 'Must log all override rationale'].map((item, i) => (
                          <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                            <span className="w-1 h-1 rounded-full bg-red-400" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-[10px] uppercase tracking-wider text-emerald-400/80 font-semibold mb-2">Authority</h4>
                      <ul className="space-y-1">
                        {['Approve / deny autonomy tiers', 'Pause or rollback agents', 'Block irreversible actions'].map((item, i) => (
                          <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                            <span className="w-1 h-1 rounded-full bg-emerald-400" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </NodeCard>

          <ConnectionLine />

          {/* Policy Engine & Tool Gateway */}
          <div className="grid md:grid-cols-2 gap-3">
            <NodeCard id="policy" glowColor="#F59E0B">
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                  <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">Policy Engine</h3>
                </div>
                <div className="flex flex-wrap gap-1">
                  {['ABAC + Guardrails', 'Autonomy Tiers', 'Approval Gates', 'Tool Constraints'].map((item, i) => (
                    <Badge key={i} color="#F59E0B">{item}</Badge>
                  ))}
                </div>
              </div>
            </NodeCard>

            <NodeCard id="gateway" glowColor="#EC4899">
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 rounded-full bg-pink-400" />
                  <h3 className="text-sm font-semibold text-pink-400 uppercase tracking-wider">Tool Gateway</h3>
                </div>
                <div className="flex flex-wrap gap-1">
                  {['Short-Lived Credentials', 'Strict Allowlists', 'Spend / Scope Limits'].map((item, i) => (
                    <Badge key={i} color="#EC4899">{item}</Badge>
                  ))}
                </div>
              </div>
            </NodeCard>
          </div>

          <ConnectionLine />

          {/* External APIs & Outputs */}
          <div className="grid md:grid-cols-3 gap-3">
            <NodeCard id="external" glowColor="#64748B">
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 rounded-full bg-slate-400" />
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">External APIs / Tools</h3>
                </div>
                <div className="flex flex-wrap gap-1">
                  {['Anthropic', 'ElevenLabs', 'OpenRouter', '...'].map((item, i) => (
                    <span key={i} className="text-xs text-slate-300 bg-slate-800/50 px-2 py-1 rounded-lg">{item}</span>
                  ))}
                </div>
              </div>
            </NodeCard>

            <NodeCard id="tool_gateway_exec" glowColor="#EC4899">
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 rounded-full bg-pink-400" />
                  <h3 className="text-xs font-semibold text-pink-400 uppercase tracking-wider">Execution Choke-Point</h3>
                </div>
                <div className="text-[10px] text-slate-500">
                  Short-Lived Credentials \u2022 Spend/Scope Limits
                </div>
              </div>
            </NodeCard>

            <NodeCard id="outputs" glowColor="#22D3EE">
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 rounded-full bg-cyan-400" />
                  <h3 className="text-xs font-semibold text-cyan-400 uppercase tracking-wider">Proactive Outputs</h3>
                </div>
                <div className="flex flex-wrap gap-1">
                  {['Calendar Events', 'Compliance Reports', 'Anomalies & Alerts'].map((item, i) => (
                    <span key={i} className="text-xs text-slate-300 bg-slate-800/50 px-2 py-1 rounded-lg">{item}</span>
                  ))}
                </div>
              </div>
            </NodeCard>
          </div>

          <ConnectionLine />

          {/* Audit & Telemetry Layer */}
          <NodeCard id="audit" glowColor="#A855F7">
            <div className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
                    <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider">Audit & Telemetry Layer</h3>
                    <span className="text-[10px] text-slate-500 italic">Always-On</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {['Immutable Logs', 'Compliance Reports', 'Anomaly Detection'].map((item, i) => (
                      <span key={i} className="text-xs text-slate-300">{item}</span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  {[
                    { label: 'SOC2', bg: '#3B82F6' },
                    { label: 'ISO', bg: '#10B981' },
                    { label: 'GDPR', bg: '#8B5CF6' }
                  ].map((badge, i) => (
                    <div key={i} className="w-12 h-12 rounded-xl flex items-center justify-center text-[10px] font-bold text-white"
                      style={{ background: `${badge.bg}30`, border: `1px solid ${badge.bg}50` }}>
                      {badge.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </NodeCard>
        </div>

        {/* Click hint */}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-500">
            {selectedNode ? 'Tap anywhere else to close' : 'Tap on any component to explore details'}
          </p>
        </div>

        {/* Detail Panel */}
        {selectedNode && nodeDetails[selectedNode] && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedNode(null)}
            onTouchEnd={(e) => { e.preventDefault(); setSelectedNode(null); }}
          >
            <div
              className="relative max-w-md w-full rounded-2xl p-6 border"
              style={{
                background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
                borderColor: 'rgba(148, 163, 184, 0.3)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              }}
              onClick={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                onClick={() => setSelectedNode(null)}
                onTouchEnd={(e) => { e.preventDefault(); setSelectedNode(null); }}
                aria-label="Close details"
              >
                ✕
              </button>

              {/* Content */}
              <h3 className="text-xl font-bold text-white mb-2">
                {nodeDetails[selectedNode].title}
              </h3>

              {nodeDetails[selectedNode].subtitle && (
                <p className="text-sm text-slate-400 mb-3">{nodeDetails[selectedNode].subtitle}</p>
              )}

              {nodeDetails[selectedNode].description && (
                <p className="text-sm text-slate-300 mb-4 leading-relaxed">
                  {nodeDetails[selectedNode].description}
                </p>
              )}

              {nodeDetails[selectedNode].items && (
                <div className="mb-4">
                  <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Features</h4>
                  <ul className="space-y-1">
                    {nodeDetails[selectedNode].items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {nodeDetails[selectedNode].veto && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm"
                  style={{
                    background: `${nodeDetails[selectedNode].color}15`,
                    color: nodeDetails[selectedNode].color,
                    border: `1px solid ${nodeDetails[selectedNode].color}30`
                  }}>
                  <span className="font-medium">VETO:</span> {nodeDetails[selectedNode].veto}
                </div>
              )}

              {nodeDetails[selectedNode].constraints && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-red-400/80 font-semibold mb-2">Constraints</h4>
                    <ul className="space-y-1">
                      {nodeDetails[selectedNode].constraints.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-emerald-400/80 font-semibold mb-2">Authority</h4>
                    <ul className="space-y-1">
                      {nodeDetails[selectedNode].authority.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {nodeDetails[selectedNode].compliance && (
                <div className="flex gap-2 mt-4">
                  {nodeDetails[selectedNode].compliance.map((item, i) => (
                    <span key={i} className="px-3 py-1 rounded-lg text-xs font-medium bg-slate-700/50 text-slate-300">
                      {item}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CAOArchitectureDiagram;
